import type { TownData } from '@/game/types';
import type { ReadonlyData } from '@/modules/game-domain/render-snapshot';
import { worldToScreen } from '@/modules/game-renderer/coordinates';

export const GAME_ZOOM_STEPS = [.25, .375, .5, .75, 1] as const;
export const MIN_GAME_ZOOM = GAME_ZOOM_STEPS[0];
export const MAX_GAME_ZOOM = GAME_ZOOM_STEPS[GAME_ZOOM_STEPS.length - 1]!;

/** Manual buttons use the nearest strictly adjacent normal step, including after an arbitrary fitted zoom. */
export function adjacentGameZoom(zoom: number, direction: 'in' | 'out'): number | null {
  if (!Number.isFinite(zoom)) return null;
  if (direction === 'in') {
    for (const step of GAME_ZOOM_STEPS) if (step > zoom) return step;
  } else {
    for (let i = GAME_ZOOM_STEPS.length - 1; i >= 0; i--) if (GAME_ZOOM_STEPS[i]! < zoom) return GAME_ZOOM_STEPS[i]!;
  }
  return null;
}
/** Allows the largest saved towns to fit on narrow mobile viewports. */
export const MIN_TOWN_OVERVIEW_ZOOM = 0.05;
export const clampGameZoom = (zoom: number, minimum: number = MIN_GAME_ZOOM): number => Number.isFinite(zoom)
  ? Math.max(minimum, Math.min(MAX_GAME_ZOOM, zoom)) : MAX_GAME_ZOOM;

export interface CameraControlsSnapshot {
  readonly zoom: number;
  readonly minimum: number;
  readonly inTown: boolean;
  readonly overview: boolean;
}

export interface CameraInsets { top: number; right: number; bottom: number; left: number }
/** Bounds include roof overhang above contacts, streets, plazas and roadside props. */
export function fitTownCamera(
  town: ReadonlyData<TownData>,
  viewport: { width: number; height: number },
  insets: CameraInsets = { top: 150, right: 24, bottom: 100, left: 24 },
  roofOverhangPx = 190,
): { x: number; y: number; zoom: number } {
  const points: Array<{ x: number; y: number }> = [];
  const add = (p: { x: number; y: number }) => points.push(worldToScreen(p.x, p.y));
  for (const street of town.authored?.streets ?? []) for (const p of street.points) {
    for (const dx of [-street.width / 2, street.width / 2])
      for (const dy of [-street.width / 2, street.width / 2]) add({ x: p.x + dx, y: p.y + dy });
  }
  for (const p of town.authored?.fortifications?.perimeter ?? []) {
    // Wall thickness and corner towers extend beyond their ground contact polygon.
    for (const dx of [-2, 2]) for (const dy of [-2, 2]) add({ x: p.x + dx, y: p.y + dy });
  }
  for (const plaza of town.authored?.plazas ?? []) for (const p of plaza.polygon) add(p);
  for (const b of town.buildings ?? []) {
    const bounds = b.authored?.footprint ?? { minX: b.worldX - b.footprintW,
      maxX: b.worldX, minY: b.worldY - b.footprintH, maxY: b.worldY };
    for (const x of [bounds.minX, bounds.maxX]) for (const y of [bounds.minY, bounds.maxY]) add({ x, y });
  }
  for (const p of [...(town.objects ?? []), ...(town.npcs ?? []), ...(town.ambientNPCs ?? [])]) points.push(worldToScreen(p.worldX, p.worldY));
  if (!points.length) {
    for (const x of [-town.radius, town.radius]) for (const y of [-town.radius, town.radius]) add({ x: town.centerX + x, y: town.centerY + y });
  }
  const minX = Math.min(...points.map(p => p.x)) - 80;
  const maxX = Math.max(...points.map(p => p.x)) + 80;
  const minY = Math.min(...points.map(p => p.y)) - roofOverhangPx;
  const maxY = Math.max(...points.map(p => p.y)) + 50;
  const width = Math.max(1, viewport.width - insets.left - insets.right);
  const height = Math.max(1, viewport.height - insets.top - insets.bottom);
  const zoom = clampGameZoom(Math.min(width / (maxX - minX), height / (maxY - minY)), MIN_TOWN_OVERVIEW_ZOOM);
  return { zoom, x: (minX + maxX) / 2 - (insets.left - insets.right) / (2 * zoom),
    y: (minY + maxY) / 2 - (insets.top - insets.bottom) / (2 * zoom) };
}
